/**
 * My Student Detail Page — Parent View
 * Layout mirrors the admin StudentDetail for visual consistency.
 * Read-only view with a side drawer for editing permitted fields.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription } from '../ui/sheet';
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from '../ui/form';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';

import {
  ArrowLeft, Edit, Save, Loader2, User, Phone, MapPin,
  GraduationCap, Bus, Award, Home, Mail, UserCheck, AlertTriangle, Archive,
} from 'lucide-react';

import { useQuery } from '@tanstack/react-query';
import { useStudent, useUpdateStudent } from '../../hooks/useStudents';
import { api } from '../../lib/api';
import { useMyFieldPermissions } from '../../hooks/usePermissions';
import { FriendshipPanel } from '../students/FriendshipPanel';
import { PaymentsPanel } from '../students/PaymentsPanel';
import { HouseAssignmentPanel, FriendGroupCard } from '../students/HouseAssignmentPanel';
import { ClassAssignmentPanel } from '../students/ClassAssignmentPanel';
import { EmailLogPanel } from '../students/EmailLogPanel';

// ---------------------------------------------------------------------------
// Schema — all optional; backend enforces required fields
// ---------------------------------------------------------------------------
const schema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  birthday: z.string().optional(),
  gender: z.string().optional(),
  home_county: z.string().optional(),
  school: z.string().optional(),
  school_year: z.string().optional(),
  student_email: z.string().email().optional().or(z.literal('')),
  parent_name: z.string().optional(),
  parent_email: z.string().email().optional().or(z.literal('')),
  parent_phone: z.string().optional(),
  address_1: z.string().optional(),
  address_2: z.string().optional(),
  address_3: z.string().optional(),
  address_4: z.string().optional(),
  eircode: z.string().optional(),
  travel_required: z.boolean().optional(),
  travel_location: z.string().optional(),
  attended_before: z.boolean().optional(),
  when_attended: z.string().optional(),
  has_friends: z.boolean().optional(),
  notes: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.travel_required && (!data.travel_location || data.travel_location.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Travel location is required when travel is needed',
      path: ['travel_location'],
    });
  }
});

// ---------------------------------------------------------------------------
// Pull a displayable message out of a DRF error response — falls back through
// detail/message, then the first field-level validation error (DRF returns
// those as e.g. { student_email: ["A student with this email already exists."] }
// with no detail/message key, so without this parents just saw a generic error).
// ---------------------------------------------------------------------------
function extractErrorMessage(err) {
  const data = err.response?.data;
  if (!data) return null;
  if (data.detail) return data.detail;
  if (data.message) return data.message;
  const firstFieldError = Object.values(data).find((v) => Array.isArray(v) && v.length > 0);
  return firstFieldError ? firstFieldError[0] : null;
}

// ---------------------------------------------------------------------------
// Shared section card — matches admin StudentDetail styling exactly
// ---------------------------------------------------------------------------
const SectionCard = ({ title, icon: Icon, children, className = '' }) => (
  <Card className={className}>
    <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
      <CardTitle className="flex items-center text-sm font-semibold text-foreground">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
          <Icon className="h-4 w-4 text-[#F28482]" />
        </span>
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-4">
      {children}
    </CardContent>
  </Card>
);

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IE', { year: 'numeric', month: 'long', day: 'numeric' });
}

function calculateAge(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  const dob = new Date(dateStr);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function MyStudentDetail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: student, isLoading, error, refetch } = useStudent(uuid);
  const { data: fieldPerms, isLoading: fieldPermsLoading } = useMyFieldPermissions();
  const { data: travelLocationsData } = useQuery({
    queryKey: ['travel-locations'],
    queryFn: () => api.travelLocations.getAll(),
    staleTime: 60 * 60 * 1000,
  });
  const travelLocations = travelLocationsData?.results || travelLocationsData || [];

  const { data: schoolYearsData } = useQuery({
    queryKey: ['school-years'],
    queryFn: () => api.schoolYears.getAll(),
    staleTime: 60 * 60 * 1000,
  });
  const schoolYears = schoolYearsData?.results || schoolYearsData || [];
  const updateMutation = useUpdateStudent();

  const editableFields = new Set(fieldPerms?.editable_fields || []);
  const canEdit = (field) => editableFields.has(field);
  const isCancelled = student?.status === 'CANCELLED' || student?.has_canceled;
  const isArchived = student?.status === 'ARCHIVED';
  // Show Edit button optimistically while permissions are loading, but never for cancelled or archived (past) students
  const hasAnyEditable = !isCancelled && !isArchived && (fieldPermsLoading || editableFields.size > 0);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '', last_name: '', birthday: '', gender: '',
      home_county: '', school: '', school_year: '', student_email: '',
      parent_name: '', parent_email: '', parent_phone: '',
      address_1: '', address_2: '', address_3: '', address_4: '',
      eircode: '', travel_required: false, travel_location: '',
      attended_before: false, when_attended: '', has_friends: false, notes: '',
    },
  });

  useEffect(() => {
    if (student) {
      form.reset({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        birthday: student.birthday || '',
        gender: student.gender || '',
        home_county: student.home_county || '',
        school: student.school || '',
        school_year: student.school_year || '',
        student_email: student.student_email || '',
        parent_name: student.parent_name || '',
        parent_email: student.parent_email || '',
        parent_phone: student.parent_phone || '',
        address_1: student.address_1 || '',
        address_2: student.address_2 || '',
        address_3: student.address_3 || '',
        address_4: student.address_4 || '',
        eircode: student.eircode || '',
        travel_required: student.travel_required || false,
        travel_location: student.travel_location || '',
        attended_before: student.attended_before || false,
        when_attended: student.when_attended || '',
        has_friends: student.has_friends || false,
        notes: student.notes || '',
      });
    }
  }, [student, form]);

  const handleSave = form.handleSubmit(async (data) => {
    try {
      await updateMutation.mutateAsync({
        id: uuid,
        first_name: data.first_name,
        last_name: data.last_name,
        birthday: data.birthday || student.birthday,
        gender: data.gender,
        home_county: data.home_county || '',
        school: data.school || '',
        school_year: data.school_year || student.school_year,
        student_email: data.student_email || '',
        parent_name: data.parent_name || '',
        parent_email: data.parent_email || '',
        parent_phone: data.parent_phone || '',
        address_1: data.address_1 || '',
        address_2: data.address_2 || '',
        address_3: data.address_3 || '',
        address_4: data.address_4 || '',
        eircode: data.eircode || '',
        travel_required: data.travel_required,
        travel_location: data.travel_required ? (data.travel_location || null) : null,
        attended_before: data.attended_before,
        when_attended: data.attended_before ? (data.when_attended || '') : '',
        has_friends: data.has_friends,
        notes: data.notes || '',
      });
      setDrawerOpen(false);
      refetch();
      toast({
        title: t('common.success'),
        description: `${data.first_name} ${data.last_name} ${t('myStudent.saveSuccess')}`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('common.error'),
        description: extractErrorMessage(err) || t('myStudent.saveError'),
        variant: 'destructive',
      });
    }
  });

  // ── Loading / error states ──────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-[#F28482]" />
          <p className="text-muted-foreground text-sm">{t('students.loading.details')}</p>
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="flex items-center justify-center min-h-64 text-center">
        <div>
          <p className="text-destructive text-sm mb-4">{t('common.errorLoading')}</p>
          <Button variant="outline" onClick={() => navigate('/family-dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  const initials =
    ((student.first_name?.[0] || '').toUpperCase()) +
    ((student.last_name?.[0] || '').toUpperCase());

  const age = calculateAge(student.birthday);

  // ── JSX ────────────────────────────────────────────────────────────────
  return (
    <div className="py-4 space-y-4">

      {/* ── Past student (archived) banner ──────────────────────────────── */}
      {isArchived && (
        <div className="-mx-3 sm:-mx-6 -mt-4 bg-gray-500 dark:bg-gray-600 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
          <Archive className="h-4 w-4 shrink-0" />
          <span>{t('myStudent.pastStudentBanner')}</span>
        </div>
      )}

      {/* ── Missing-data banners — not shown for past/read-only students ──── */}
      {!isArchived && (!student.student_email || !student.parent_phone || !student.address_1) && (
        <div className="-mx-3 sm:-mx-6 -mt-4 flex flex-col">
          {!student.student_email && (
            <div className="bg-orange-500 dark:bg-orange-600 text-white px-4 py-2 flex items-center justify-between text-sm font-medium">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{t('myStudent.missingEmailBanner')}</span>
              </div>
              {canEdit('student_email') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(true)}
                  className="border-orange-200 bg-transparent text-white hover:bg-orange-600 dark:hover:bg-orange-700 h-7 ml-4 shrink-0"
                >
                  {t('myStudent.addEmail')}
                </Button>
              )}
            </div>
          )}
          {!student.parent_phone && (
            <div className={`bg-orange-500 dark:bg-orange-600 text-white px-4 py-2 flex items-center justify-between text-sm font-medium ${!student.student_email ? 'border-t border-white/20' : ''}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{t('myStudent.missingPhoneBanner')}</span>
              </div>
              {canEdit('parent_phone') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(true)}
                  className="border-orange-200 bg-transparent text-white hover:bg-orange-600 dark:hover:bg-orange-700 h-7 ml-4 shrink-0"
                >
                  {t('myStudent.addPhone')}
                </Button>
              )}
            </div>
          )}
          {!student.address_1 && (
            <div className={`bg-orange-500 dark:bg-orange-600 text-white px-4 py-2 flex items-center justify-between text-sm font-medium ${(!student.student_email || !student.parent_phone) ? 'border-t border-white/20' : ''}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>{t('myStudent.missingAddressBanner')}</span>
              </div>
              {(canEdit('address_1') || canEdit('travel_required')) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDrawerOpen(true)}
                  className="border-orange-200 bg-transparent text-white hover:bg-orange-600 dark:hover:bg-orange-700 h-7 ml-4 shrink-0"
                >
                  {t('myStudent.addDetails')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Hero strip ──────────────────────────────────────────────────── */}
      <div
        className="rounded-xl px-5 py-4"
        style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-xl bg-black/10 flex items-center justify-center border-2 border-black/10 shrink-0">
              <span className="text-xl font-bold text-gray-800 leading-none">{initials}</span>
            </div>

            {/* Name + ID + badges */}
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-gray-900">
                  {student.first_name} {student.last_name}
                </h1>
                {student.is_cinnire && (
                  <Badge className="bg-amber-600 hover:bg-amber-600 text-white border-transparent">
                    <Award className="h-3 w-3 mr-1" />
                    {t('students.badge.cinnire')}
                  </Badge>
                )}
                {isArchived && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40">
                    <Archive className="h-3 w-3 mr-1" />
                    {t('familyDashboard.pastStudent')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                {student.student_id && (
                  <p className="text-gray-700 text-sm font-mono">{student.student_id}</p>
                )}
                {student.course_name && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <GraduationCap className="h-3 w-3 mr-1" />{student.course_name}
                  </Badge>
                )}
                {student.house_name && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <Home className="h-3 w-3 mr-1" />{student.house_name}
                  </Badge>
                )}
                {student.travel_required && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <Bus className="h-3 w-3 mr-1" />{student.travel_location_name || t('students.badge.travelRequired')}
                  </Badge>
                )}
                {student.has_friends && (
                  <Badge variant="outline" className="border-black/20 text-gray-800 bg-white/40 text-xs">
                    <UserCheck className="h-3 w-3 mr-1" />{t('students.badge.hasFriends')}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
            <Button
              variant="outline"
              size="sm"
              className="bg-white/60 border-black/20 text-gray-800 hover:bg-white/80 dark:bg-white/60 dark:border-black/20 dark:text-gray-800 dark:hover:bg-white/80 min-h-[44px]"
              onClick={() => navigate('/family-dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('myStudent.back')}
            </Button>
            {hasAnyEditable && (
              <Button
                size="sm"
                className="bg-white hover:bg-white/90 text-gray-900 border border-black/10 min-h-[44px]"
                onClick={() => setDrawerOpen(true)}
              >
                <Edit className="h-4 w-4 mr-1.5" />
                {t('students.edit.editProfile')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="pb-8 space-y-4">

        {/* Row 1 — Contact + Academic */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title={t('students.labels.contactInformation')} icon={Phone}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              <div>
                <p className="text-xs text-muted-foreground">{t('students.labels.parentGuardian')}</p>
                <p className="text-sm font-medium text-foreground">{student.parent_name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />{t('students.labels.email')}
                </p>
                <p className="text-sm text-foreground truncate">{student.parent_email || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" />{t('students.labels.phone')}
                </p>
                <p className="text-sm font-medium text-foreground">{student.parent_phone || '—'}</p>
              </div>
              {(student.address_1 || student.address_2) && (
                <div className="col-span-2 sm:col-span-3 pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <MapPin className="h-3 w-3" />{t('students.detail.address')}
                  </p>
                  {[student.address_1, student.address_2, student.address_3, student.address_4]
                    .filter(Boolean)
                    .map((line, i) => (
                      <p key={i} className="text-xs text-foreground">{line}</p>
                    ))}
                  {student.eircode && (
                    <p className="text-xs font-mono text-foreground">{student.eircode}</p>
                  )}
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard title={t('students.detail.academicInfo')} icon={GraduationCap}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.dateOfBirth')}</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(student.birthday)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.age')}</p>
                  <p className="text-sm font-medium text-foreground">
                    {age !== null ? `${age} ${t('students.labels.yearsOld')}` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.gender')}</p>
                  <p className="text-sm font-medium text-foreground">
                    {student.gender === 'M' ? t('common.male')
                    : student.gender === 'F' ? t('common.female')
                    : student.gender || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.homeCounty')}</p>
                  <p className="text-sm font-medium text-foreground">{student.home_county || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">{t('students.form.studentEmail')}</p>
                  <p className="text-sm text-foreground">{student.student_email || '—'}</p>
                </div>
              </div>
              <div className="border-t border-border pt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.course')}</p>
                  <p className="text-sm text-foreground">{student.course_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.school')}</p>
                  <p className="text-sm text-foreground">{student.school || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('students.form.schoolYear')}</p>
                  <p className="text-sm text-foreground">{student.school_year_name || '—'}</p>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Row 2 — House · Class · Travel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <HouseAssignmentPanel student={student} canEdit={false} />
          <ClassAssignmentPanel student={student} canEdit={false} />
          <SectionCard title={t('students.detail.travel')} icon={Bus}>
            <div className="divide-y divide-border">
              <div className="flex items-center justify-between py-3 first:pt-0">
                <p className="text-xs text-muted-foreground">{t('students.form.travelRequired')}</p>
                {student.travel_required ? (
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <Badge className="bg-sky-500 hover:bg-sky-500 text-xs">
                      <Bus className="h-3 w-3 mr-1" />{t('students.badge.yes')}
                    </Badge>
                    {student.travel_location_name && (
                      <span className="text-sm flex items-center gap-1 text-foreground">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        {student.travel_location_name}
                      </span>
                    )}
                  </div>
                ) : (
                  <Badge variant="outline" className="text-xs">{t('students.badge.no')}</Badge>
                )}
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Row 3 — Friends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <FriendshipPanel student={student} readOnly={true} emptyDescription={t('friendships.comingSoon')} />
            <FriendGroupCard student={student} />
          </div>
        </div>

        {/* Full-width tail */}
        <PaymentsPanel student={student} readOnly={true} />
        <EmailLogPanel student={student} />

      </div>

      {/* ── Edit Drawer ─────────────────────────────────────────────────── */}
      <Sheet open={drawerOpen} onOpenChange={(open) => {
        if (!open && form.formState.isDirty) {
          if (!window.confirm(t('students.unsavedChanges.discard'))) return;
          form.reset();
        }
        setDrawerOpen(open);
      }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>
              <Edit className="h-4 w-4 inline mr-2" />
              {t('students.edit.editProfile')} — {student.first_name} {student.last_name}
            </SheetTitle>
            <SheetDescription>
              {t('students.edit.editProfileDesc', 'Update your details below')}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            <Form {...form}>

              {/* Personal */}
              {(canEdit('first_name') || canEdit('last_name') || canEdit('birthday') || canEdit('gender') || canEdit('home_county') || canEdit('school') || canEdit('school_year') || canEdit('student_email')) && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />{t('students.detail.personalInfo')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {canEdit('first_name') && (
                      <FormField control={form.control} name="first_name" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.form.firstName')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('last_name') && (
                      <FormField control={form.control} name="last_name" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.form.lastName')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('birthday') && (
                      <FormField control={form.control} name="birthday" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.form.dateOfBirth')}</FormLabel><FormControl><Input {...field} type="date" /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('gender') && (
                      <FormField control={form.control} name="gender" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.form.gender')}</FormLabel>
                          <FormControl>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectGender')} /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="M">{t('common.male')}</SelectItem>
                                <SelectItem value="F">{t('common.female')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl><FormMessage />
                        </FormItem>
                      )} />
                    )}
                    {canEdit('home_county') && (
                      <FormField control={form.control} name="home_county" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.form.homeCounty')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('school') && (
                      <FormField control={form.control} name="school" render={({ field }) => (
                        <FormItem className="col-span-2"><FormLabel>{t('students.form.school')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('school_year') && (
                      <FormField control={form.control} name="school_year" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.form.schoolYear')}</FormLabel>
                          <FormControl>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectSchoolYear')} /></SelectTrigger>
                              <SelectContent>
                                {schoolYears.map(sy => <SelectItem key={sy.uuid} value={sy.uuid}>{sy.year}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </FormControl><FormMessage />
                        </FormItem>
                      )} />
                    )}
                    {canEdit('student_email') && (
                      <FormField control={form.control} name="student_email" render={({ field }) => (
                        <FormItem className="col-span-2"><FormLabel>{t('students.form.studentEmail')}</FormLabel><FormControl><Input {...field} type="email" /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                  </div>
                </div>
              )}

              {/* Contact */}
              {(canEdit('parent_name') || canEdit('parent_email') || canEdit('parent_phone')) && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <Phone className="h-4 w-4" />{t('students.labels.contactInformation')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {canEdit('parent_name') && (
                      <FormField control={form.control} name="parent_name" render={({ field }) => (
                        <FormItem className="col-span-2"><FormLabel>{t('students.labels.parentGuardian')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('parent_email') && (
                      <FormField control={form.control} name="parent_email" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.labels.email')}</FormLabel><FormControl><Input {...field} type="email" /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('parent_phone') && (
                      <FormField control={form.control} name="parent_phone" render={({ field }) => (
                        <FormItem><FormLabel>{t('students.labels.phone')}</FormLabel><FormControl><Input {...field} type="tel" /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                  </div>
                </div>
              )}

              {/* Address */}
              {(canEdit('address_1') || canEdit('address_2') || canEdit('address_3') || canEdit('address_4') || canEdit('eircode')) && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />{t('students.detail.address')}
                  </h3>
                  <div className="space-y-3">
                    {canEdit('address_1') && (
                      <FormField control={form.control} name="address_1" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">{t('students.form.addressLine1')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('address_2') && (
                      <FormField control={form.control} name="address_2" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">{t('students.form.addressLine2')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('address_3') && (
                      <FormField control={form.control} name="address_3" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">{t('students.form.addressLine3')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('address_4') && (
                      <FormField control={form.control} name="address_4" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">{t('students.form.addressLine4')}</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('eircode') && (
                      <FormField control={form.control} name="eircode" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">{t('students.form.eircode')}</FormLabel><FormControl><Input {...field} className="font-mono" /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                  </div>
                </div>
              )}

              {/* Travel */}
              {(canEdit('travel_required') || canEdit('travel_location')) && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <Bus className="h-4 w-4" />{t('students.detail.travel')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {canEdit('travel_required') && (
                      <FormField control={form.control} name="travel_required" render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                          <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                          <FormLabel className="cursor-pointer">{t('students.form.travelRequired')}</FormLabel>
                        </FormItem>
                      )} />
                    )}
                    {canEdit('travel_location') && form.watch('travel_required') && (
                      <FormField control={form.control} name="travel_location" render={({ field }) => (
                        <FormItem className="col-span-2"><FormLabel>{t('students.form.travelLocation')}</FormLabel>
                          <FormControl>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <SelectTrigger><SelectValue placeholder={t('students.placeholders.selectTravelLocation')} /></SelectTrigger>
                              <SelectContent>
                                {travelLocations?.map(loc => (
                                  <SelectItem key={loc.uuid} value={loc.uuid}>{loc.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl><FormMessage />
                        </FormItem>
                      )} />
                    )}
                  </div>
                </div>
              )}

              {/* Other */}
              {(canEdit('attended_before') || canEdit('has_friends') || canEdit('notes')) && (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider border-b border-border pb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />{t('students.detail.other', 'Other')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {canEdit('attended_before') && (
                      <FormField control={form.control} name="attended_before" render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                          <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                          <FormLabel className="cursor-pointer">{t('students.form.previousAttendance')}</FormLabel>
                        </FormItem>
                      )} />
                    )}
                    {canEdit('has_friends') && (
                      <FormField control={form.control} name="has_friends" render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                          <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                          <FormLabel className="cursor-pointer">{t('students.form.hasFriends', 'Has friends')}</FormLabel>
                        </FormItem>
                      )} />
                    )}
                    {canEdit('attended_before') && form.watch('attended_before') && (
                      <FormField control={form.control} name="when_attended" render={({ field }) => (
                        <FormItem className="col-span-2"><FormLabel>{t('students.form.whenAttended', 'When attended')}</FormLabel><FormControl><Input {...field} placeholder="e.g. Summer 2023" /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                    {canEdit('notes') && (
                      <FormField control={form.control} name="notes" render={({ field }) => (
                        <FormItem className="col-span-2"><FormLabel>{t('students.detail.notes')}</FormLabel><FormControl><Textarea {...field} rows={3} placeholder={t('students.placeholders.addNotes')} /></FormControl><FormMessage /></FormItem>
                      )} />
                    )}
                  </div>
                </div>
              )}

            </Form>
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => {
              if (form.formState.isDirty && !window.confirm(t('students.unsavedChanges.discard'))) return;
              form.reset();
              setDrawerOpen(false);
            }}>
              {t('students.edit.cancelChanges')}
            </Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('students.edit.saving')}</>
                : <><Save className="h-4 w-4 mr-2" />{t('students.edit.saveChanges')}</>
              }
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

    </div>
  );
}

export default MyStudentDetail;
